import { useState, useEffect } from 'react';
import { Mail, TestTube, CheckCircle, XCircle, Server, RefreshCw, Eye, EyeOff } from 'lucide-react';
import { Button, Modal } from './ui';
import { api } from '../lib/api';
import toast from 'react-hot-toast';

interface UserEmailCredentials {
  id?: number;
  email_address: string;
  email_username: string;
  display_name?: string;
  signature?: string;
  is_default: boolean;
  enabled: boolean;
  last_tested?: string;
  test_status?: 'untested' | 'success' | 'failed';
}

interface SystemEmailSettings {
  smtp_host: string;
  smtp_port: number;
  smtp_secure: 'none' | 'tls' | 'ssl';
  smtp_auth_required: boolean;
  imap_host?: string;
  imap_port?: number;
  imap_secure?: 'none' | 'tls' | 'ssl';
  sender_name?: string;
  enabled: boolean;
}

interface EmailCredentialModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (credentials: UserEmailCredentials & { email_password?: string }) => Promise<void>;
  credential: UserEmailCredentials | null;
  requiredForSending?: boolean;
}

const EmailCredentialModal: React.FC<EmailCredentialModalProps> = ({
  isOpen,
  onClose,
  onSave,
  credential,
  requiredForSending = false
}) => {
  const [formData, setFormData] = useState<UserEmailCredentials & { email_password?: string }>({
    email_address: '',
    email_username: '',
    email_password: '',
    display_name: '',
    signature: '',
    is_default: false,
    enabled: true
  });
  const [showPassword, setShowPassword] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [systemSettings, setSystemSettings] = useState<SystemEmailSettings | null>(null);
  const [loadingSystemSettings, setLoadingSystemSettings] = useState(false);
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);
  const [isTesting, setIsTesting] = useState(false);

  useEffect(() => {
    if (credential) {
      setFormData({
        ...credential,
        email_password: '' // Don't prefill password for security
      });
    } else {
      setFormData({
        email_address: '',
        email_username: '',
        email_password: '',
        display_name: '',
        signature: '',
        is_default: false,
        enabled: true
      });
    }
  }, [credential, isOpen]);

  // Fetch system settings when modal opens
  useEffect(() => {
    if (isOpen && !systemSettings) {
      fetchSystemSettings();
    }
  }, [isOpen]);

  const fetchSystemSettings = async () => {
    setLoadingSystemSettings(true);
    try {
      const response = await api.get('/email/system-settings');
      if (response.data.success && response.data.data) {
        setSystemSettings(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching system email settings:', error);
    } finally {
      setLoadingSystemSettings(false);
    }
  };

  const handleUseSystemSettings = () => {
    if (!systemSettings) {
      toast.error('Systemeinstellungen nicht verfügbar');
      return;
    }

    // Auto-populate email username from email address if not already set
    const emailUsername = formData.email_username || formData.email_address;

    setFormData(prev => ({
      ...prev,
      email_username: emailUsername
    }));

    toast.success('Systemeinstellungen übernommen');
  };

  const handleTestConnection = async () => {
    console.log('🔍 Test button clicked, checking form data...');
    console.log('Form data state:', {
      email_address: formData.email_address,
      email_username: formData.email_username,
      has_password: !!formData.email_password
    });

    if (!formData.email_address || formData.email_address.trim() === '') {
      toast.error('Bitte geben Sie zuerst eine E-Mail-Adresse ein');
      return;
    }

    if (!formData.email_username || formData.email_username.trim() === '') {
      toast.error('Bitte geben Sie einen Benutzernamen ein');
      return;
    }

    if (!formData.email_password || formData.email_password.trim() === '') {
      toast.error('Bitte geben Sie ein Passwort ein');
      return;
    }

    setIsTesting(true);
    setTestResult(null);

    console.log('🔍 Frontend form data for test:', {
      email_address: formData.email_address,
      email_username: formData.email_username,
      has_password: !!formData.email_password,
      password_length: formData.email_password?.length
    });

    try {
      const testData = {
        email_address: formData.email_address,
        email_username: formData.email_username,
        email_password: formData.email_password
      };
      
      console.log('🚀 Sending test request:', {
        email_address: testData.email_address,
        email_username: testData.email_username,
        has_password: !!testData.email_password
      });
      
      const response = await api.post('/email/test-connection', testData);
      
      setTestResult(response.data);
      
      if (response.data.success) {
        toast.success(response.data.message || 'E-Mail-Verbindung erfolgreich getestet', {
          duration: 6000 // Longer duration for important message
        });
      } else {
        toast.error(`Verbindungstest fehlgeschlagen: ${response.data.message}`);
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.message || 'Test fehlgeschlagen';
      setTestResult({ success: false, message: errorMessage });
      toast.error(`Verbindungstest fehlgeschlagen: ${errorMessage}`);
    } finally {
      setIsTesting(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);
    try {
      await onSave(formData);
    } finally {
      setIsSaving(false);
    }
  };

  const handleInputChange = (field: keyof (UserEmailCredentials & { email_password?: string }), value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  if (!isOpen) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={credential ? 'E-Mail-Konto bearbeiten' : 'E-Mail-Konto hinzufügen'}
      description={
        <span className="inline-flex items-center gap-2 text-gray-600 dark:text-gray-400">
          <Mail className="h-4 w-4 shrink-0 text-primary-600 dark:text-primary-400" />
          Persönliche SMTP/IMAP-Zugangsdaten
        </span>
      }
      size="lg"
      panelClassName="!max-w-2xl max-h-[min(92dvh,900px)]"
      footer={
        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={onClose} disabled={isSaving}>
            Abbrechen
          </Button>
          <Button type="submit" form="lr-email-credential-form" variant="primary" disabled={isSaving}>
            {isSaving ? (
              <span className="inline-flex items-center gap-2">
                <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                Speichern…
              </span>
            ) : credential ? (
              'Aktualisieren'
            ) : (
              'Hinzufügen'
            )}
          </Button>
        </div>
      }
    >
      <form id="lr-email-credential-form" onSubmit={handleSubmit} className="space-y-6">
          {requiredForSending && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
              <p className="text-sm text-yellow-900 font-medium">
                Hinweis: Für den E-Mail-Versand ist die Eingabe einer <strong>E-Mail-Adresse</strong> zwingend erforderlich.
              </p>
              <p className="text-xs text-yellow-800 mt-1">
                Sie können den Vorgang jederzeit durch Klick auf <strong>Abbrechen</strong> abbrechen.
              </p>
            </div>
          )}
          {/* Email Address */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              E-Mail-Adresse *
            </label>
            <input
              type="email"
              value={formData.email_address}
              onChange={(e) => handleInputChange('email_address', e.target.value)}
              className="input-field w-full min-h-[2.5rem] text-sm"
              placeholder="ihre.email@beispiel.de"
              required
            />
            {requiredForSending && (
              <p className="text-xs text-gray-600 mt-1">
                Diese Adresse wird als Absender verwendet (und erhält Test-E-Mails / BCC je nach Einstellung).
              </p>
            )}
          </div>

          {/* Username */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Benutzername (SMTP/IMAP) *
            </label>
            <input
              type="text"
              value={formData.email_username}
              onChange={(e) => handleInputChange('email_username', e.target.value)}
              className="input-field w-full min-h-[2.5rem] text-sm"
              placeholder="Meist identisch mit E-Mail-Adresse"
              required
            />
            <p className="text-xs text-gray-500 mt-1">
              Oft identisch mit der E-Mail-Adresse, kann aber abweichen
            </p>
          </div>

          {/* Password */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Passwort {credential ? '' : '*'}
            </label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={formData.email_password}
                onChange={(e) => handleInputChange('email_password', e.target.value)}
                className="input-field w-full min-h-[2.5rem] py-2 pr-11 text-sm"
                placeholder={credential ? 'Leer lassen, um beizubehalten' : 'Ihr E-Mail-Passwort'}
                required={!credential}
              />
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="absolute inset-y-0 right-1 my-auto h-8 w-8 shrink-0 p-0 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                onClick={() => setShowPassword(!showPassword)}
                title={showPassword ? 'Passwort verbergen' : 'Passwort anzeigen'}
                aria-label={showPassword ? 'Passwort verbergen' : 'Passwort anzeigen'}
              >
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </Button>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Für Gmail verwenden Sie ein App-spezifisches Passwort
            </p>
          </div>

          {/* System Settings Info & Auto-Fill */}
          {systemSettings && (
            <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-medium text-blue-900 flex items-center">
                  <Server className="w-4 h-4 mr-2" />
                  Systemeinstellungen verfügbar
                </h3>
                <Button type="button" size="sm" variant="primary" onClick={handleUseSystemSettings}>
                  <RefreshCw className="mr-1 h-3 w-3" />
                  Übernehmen
                </Button>
              </div>
              <div className="text-xs text-blue-800 space-y-1">
                <p><strong>SMTP:</strong> {systemSettings.smtp_host}:{systemSettings.smtp_port} ({systemSettings.smtp_secure.toUpperCase()})</p>
                {systemSettings.imap_host && (
                  <p><strong>IMAP:</strong> {systemSettings.imap_host}:{systemSettings.imap_port} ({systemSettings.imap_secure?.toUpperCase()})</p>
                )}
                <p className="text-blue-600 mt-2">
                  💡 Klicken Sie "Übernehmen", um diese Servereinstellungen zu verwenden
                </p>
              </div>
            </div>
          )}

          {/* Test Connection Section */}
          <div className="border border-gray-200 rounded-md p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-medium text-gray-900 flex items-center">
                <TestTube className="w-4 h-4 mr-2" />
                Verbindung testen
              </h3>
              <Button
                type="button"
                size="sm"
                variant="secondary"
                onClick={handleTestConnection}
                disabled={isTesting || !formData.email_address || !formData.email_username || !formData.email_password}
              >
                {isTesting ? (
                  <>
                    <span className="mr-2 inline-block h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                    Sende Test-E-Mail…
                  </>
                ) : (
                  <>
                    <TestTube className="mr-2 h-4 w-4" />
                    Test-E-Mail senden
                  </>
                )}
              </Button>
            </div>
            
            {testResult && (
              <div className={`flex items-center text-sm p-2 rounded ${
                testResult.success 
                  ? 'bg-green-50 text-green-700 border border-green-200' 
                  : 'bg-red-50 text-red-700 border border-red-200'
              }`}>
                {testResult.success ? (
                  <CheckCircle className="w-4 h-4 mr-2 flex-shrink-0" />
                ) : (
                  <XCircle className="w-4 h-4 mr-2 flex-shrink-0" />
                )}
                <span>{testResult.message}</span>
              </div>
            )}
            
            <p className="text-xs text-gray-500 mt-2">
              📧 Beim Test wird eine Test-E-Mail an Ihre eigene Adresse gesendet. Prüfen Sie Ihr Postfach, um zu bestätigen, dass die Konfiguration funktioniert.
            </p>
          </div>

          {/* Display Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Anzeigename (optional)
            </label>
            <input
              type="text"
              value={formData.display_name || ''}
              onChange={(e) => handleInputChange('display_name', e.target.value)}
              className="input-field w-full min-h-[2.5rem] text-sm"
              placeholder="Max Mustermann"
            />
            <p className="text-xs text-gray-500 mt-1">
              Name, der als Absender angezeigt wird
            </p>
          </div>

          {/* Email Signature */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              E-Mail-Signatur (optional)
            </label>
            <textarea
              value={formData.signature || ''}
              onChange={(e) => handleInputChange('signature', e.target.value)}
              rows={3}
              className="input-field w-full min-h-[2.5rem] resize-none text-sm"
              placeholder="Mit freundlichen Grüßen&#10;Max Mustermann&#10;Musterschule"
            />
          </div>

          {/* Options */}
          <div className="space-y-3">
            <div className="flex items-center">
              <input
                type="checkbox"
                id="is_default"
                checked={formData.is_default}
                onChange={(e) => handleInputChange('is_default', e.target.checked)}
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
              />
              <label htmlFor="is_default" className="ml-2 block text-sm text-gray-700">
                Als Standard-E-Mail-Konto verwenden
              </label>
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                id="enabled"
                checked={formData.enabled}
                onChange={(e) => handleInputChange('enabled', e.target.checked)}
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
              />
              <label htmlFor="enabled" className="ml-2 block text-sm text-gray-700">
                Konto aktiviert
              </label>
            </div>
          </div>


          {/* Help Text */}
          <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
            <h4 className="text-sm font-medium text-blue-900 mb-2">Hilfe zur Konfiguration:</h4>
            <ul className="text-xs text-blue-800 space-y-1">
              <li><strong>Gmail:</strong> Verwenden Sie ein App-Passwort (nicht Ihr normales Passwort)</li>
              <li><strong>Outlook/Hotmail:</strong> Aktivieren Sie "Weniger sichere Apps" oder verwenden Sie OAuth</li>
              <li><strong>Firmen-E-Mail:</strong> Fragen Sie Ihren IT-Administrator nach den korrekten Einstellungen</li>
            </ul>
          </div>

      </form>
    </Modal>
  );
};

export default EmailCredentialModal;
